import { useContext, useState, useEffect } from 'react';
import { Button, Modal, ModalBody, FormGroup } from 'reactstrap';
import {
    TextInput,
} from '~/shared/components/form/Inputs';
import { CountryStateFields } from '~/shared/components/form/CountryStateFields';
import { useForm } from 'react-hook-form';
import { postVendorAddress } from '~/services/campaign';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CampaignContext } from '~/context/campaign';
import { FundRequestContext } from '~/context/fundrequests';
import { State } from 'country-state-city';

export type AddAddressModalProps = {
    isOpen: boolean;
    toggle: () => void;
    setPayeeDetails: (details: any, newAddressId?: number) => void;
};

export const AddAddressModal = (props: AddAddressModalProps) => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ selectedPayee }, fundRequestDispatch] = useContext(FundRequestContext);
    const { isOpen, toggle } = props;
    const [loading, setLoading] = useState(false);
    const [userEditedLabel, setUserEditedLabel] = useState(false);
    const { vendorId: urlVendorId } = useParams<{ vendorId: string }>();
    const { watch, control, reset, getValues, setValue, handleSubmit } = useForm({
        defaultValues: { country: 'US' } as Record<string, any>,
    });

    // Watch address_1 to set default address_label
    const address1 = watch('address_1');
    const addressLabel = watch('address_label');

    // Update address_label when address_1 changes and the user hasn't manually edited the label
    useEffect(() => {
        if (address1 && !userEditedLabel) {
            setValue('address_label', address1);
        }
    }, [address1, userEditedLabel]);

    // Handle manual changes to address_label
    const handleAddressLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // If the user types something different from address1, mark as user-edited
        if (e.target.value !== address1) {
            setUserEditedLabel(true);
        }

        // If they delete it completely, allow syncing with address1 again
        if (e.target.value === '') {
            setUserEditedLabel(false);
        }
    };

    const getVendorId = () => {
        return urlVendorId || (selectedPayee?.id ? selectedPayee.id : null);
    };

    const submitNewAddress = async () => {
        const { city, address_label, state, state_province, country, address_1, address_2, zip_code } =
            getValues();

        const selectedCountry = country || 'US';
        const isUS = selectedCountry === 'US';

        // Validate state/province for countries that have subdivisions
        if (isUS && !state) {
            toast.error('Please select a state');
            return;
        }
        if (!isUS) {
            const hasSubdivisions = State.getStatesOfCountry(selectedCountry).length > 0;
            if (hasSubdivisions && !state_province) {
                toast.error('Please select a state/province');
                return;
            }
        }

        const formdata: any = {
            address_label: address_label || address_1, // Use address_1 as fallback if no label
            address_1,
            address_2,
            city,
            country: selectedCountry,
            state: isUS ? state : undefined,
            state_province: !isUS ? state_province : undefined,
            zip_code,
        };

        const campaignId = campaign?.campaignDetail?.campaign_id;
        const vendorId = getVendorId();

        if (!vendorId) {
            toast.error('Vendor ID not found');
            return;
        }

        try {
            setLoading(true);
            const updatedVendor = await postVendorAddress(
                formdata,
                vendorId,
                campaignId
            );
            if (updatedVendor?.error) {
                toast.error('Error creating address');
                return;
            }

            // Find the newly created address in the updated vendor data
            const newAddress = updatedVendor.vendor_addresses?.find(
                (address: any) => address.address_label === formdata.address_label
            );

            // Pass the updated vendor and new address ID back to the parent component
            props.setPayeeDetails(updatedVendor, newAddress?.id);
            reset();
            setUserEditedLabel(false);
            toggle();
            toast.success('Address created successfully');
        } catch (error) {
            toast.error('Error creating address');
        } finally {
            setLoading(false);
        }
    };

    const selectedPaymentType = watch('payment_type');

    return (
        <Modal centered size='lg' isOpen={isOpen} toggle={toggle}>
            <ModalBody>
                <h3>Add Address</h3>

                <FormGroup>
                    <TextInput
                        label='Street Address'
                        name='address_1'
                        type='text'
                        control={control}
                        required
                    />
                    <TextInput
                        label='Street Address 2'
                        name='address_2'
                        type='text'
                        control={control}
                    />
                    <CountryStateFields
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        required
                    />
                    <TextInput
                        label='Address Label'
                        name='address_label'
                        type='text'
                        helperText="for example: Main House, Grandma's House, etc."
                        control={control}
                        onChange={handleAddressLabelChange}
                    />
                </FormGroup>
                <Button color='outline-dark' onClick={toggle}>
                    Cancel
                </Button>
                <Button
                    color='primary'
                    onClick={handleSubmit(submitNewAddress)}
                    disabled={loading}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </Button>
            </ModalBody>
        </Modal>
    );
};
